/* PDP-SPEED: lazy load the PREDATOR lander videos.
 *
 * WHAT IT DOES
 *   Moves every lander video's src to data-src so the browser does not fetch it
 *   during the load event, then promotes data-src back to src through an
 *   IntersectionObserver with a 600px rootMargin, so the fetch starts well before
 *   the section is on screen.
 *
 * WHY IT IS NEEDED
 *   All eight videos are `autoplay muted loop` with preload="metadata". Chrome
 *   treats an autoplaying element as eligible to start and downloads the whole
 *   file anyway, so the preload hint does nothing. None of these is above the
 *   fold on a 33,000px document.
 *
 * THIS SCRIPT ALONE SAVES NOTHING. MEASURED, PDP-SPEED-2, 2026-09-05.
 *   Headless Chrome on the live page: the video fetches begin at 1,575 ms,
 *   started by the preload scanner straight from the markup, while the earliest
 *   a deferred script can run is DOMContentLoaded at 3,488 ms. Load window
 *   video bytes with and without this script: 7.62 MB either way, identical.
 *   Parking a download that is already in flight cannot un-send it.
 *   THE SAVING ONLY EXISTS IF THE MARKUP SHIPS data-src INSTEAD OF src, so the
 *   scanner never sees a URL. Proven: 0 requests below the fold, and the video
 *   plays on approach. This file is the WAKER for that markup, not a fetch
 *   canceller, and it must be applied together with the markup swap.
 *
 * WHAT DOES NOT CHANGE
 *   Every video keeps autoplay, muted, loop, playsinline and its poster. Once
 *   promoted it plays exactly as it does today. The design and the motion are
 *   identical; only the moment the bytes are requested moves.
 *
 * SAFETY
 *   - Runs once, guarded by a root attribute, like every other lander script.
 *   - If IntersectionObserver is missing, it promotes everything immediately,
 *     which is exactly today's behaviour, so an old browser is never worse off.
 *   - It only touches videos inside #no-predator, never the theme's own media.
 *   - A video already playing is left alone.
 *
 * No em or en dashes in this file.
 */
(function () {
  'use strict';

  var root = document.getElementById('no-predator');
  if (!root || root.hasAttribute('data-no-lazyvid')) return;
  root.setAttribute('data-no-lazyvid', '1');

  var MARGIN = '600px';

  function vids() {
    return Array.prototype.slice.call(root.querySelectorAll('video'));
  }

  /* Move src to data-src on every source and on the video itself. Called before
   * the browser has had a chance to start most of these fetches. */
  function park(v) {
    if (v.getAttribute('data-no-lazy') === 'parked' ||
        v.getAttribute('data-no-lazy') === 'live') return;
    /* PDP-SPEED-2 2026-09-05, BUG 1 OF 2. The original returned early when it
       found nothing to move, which meant a video whose data-src came from the
       MARKUP (the only form that actually saves bytes, see the header note) was
       never tagged 'parked', so wake() refused it and the observer's callback
       did nothing. Measured live: 0 of 7 videos ever woke. A video that already
       carries data-src is ALREADY parked and must be tagged as such. */
    var moved = false, has = false;
    var own = v.getAttribute('src');
    if (own) { v.setAttribute('data-src', own); v.removeAttribute('src'); moved = true; }
    if (v.getAttribute('data-src')) has = true;
    var srcs = v.querySelectorAll('source');
    for (var i = 0; i < srcs.length; i++) {
      var s = srcs[i].getAttribute('src');
      if (s) { srcs[i].setAttribute('data-src', s); srcs[i].removeAttribute('src'); moved = true; }
      if (srcs[i].getAttribute('data-src')) has = true;
    }
    if (!has) return;
    /* preload none while parked: nothing at all should be fetched yet. Only
       call load() when WE moved a source; calling it on a markup parked video
       is a pointless extra reset. */
    v.setAttribute('preload', 'none');
    v.setAttribute('data-no-lazy', 'parked');
    if (moved) { try { v.load(); } catch (e) {} }
  }

  function wake(v) {
    if (v.getAttribute('data-no-lazy') !== 'parked') return;
    var own = v.getAttribute('data-src');
    if (own) { v.setAttribute('src', own); v.removeAttribute('data-src'); }
    var srcs = v.querySelectorAll('source');
    for (var i = 0; i < srcs.length; i++) {
      var s = srcs[i].getAttribute('data-src');
      if (s) { srcs[i].setAttribute('src', s); srcs[i].removeAttribute('data-src'); }
    }
    v.setAttribute('preload', 'metadata');
    v.setAttribute('data-no-lazy', 'live');
    try { v.load(); } catch (e) {}
    /* autoplay muted loop elements start themselves once they have data, but an
     * explicit play is harmless and covers the iOS case the port already guards. */
    if (v.hasAttribute('autoplay')) {
      var p = v.play && v.play();
      if (p && p.catch) p.catch(function () {});
    }
  }

  var list = vids();
  for (var i = 0; i < list.length; i++) park(list[i]);

  if (!window.IntersectionObserver) {
    for (var j = 0; j < list.length; j++) wake(list[j]);
    return;
  }

  var io = new IntersectionObserver(function (entries) {
    for (var k = 0; k < entries.length; k++) {
      if (entries[k].isIntersecting) {
        wake(entries[k].target);
        io.unobserve(entries[k].target);
      }
    }
  }, { root: null, rootMargin: MARGIN, threshold: 0 });

  for (var m = 0; m < list.length; m++) io.observe(list[m]);

  /* Videos added later (the lander swaps some tier states in place) get parked
   * and observed too, so nothing added after load escapes the treatment. */
  if (window.MutationObserver) {
    var mo = new MutationObserver(function () {
      var now = vids();
      for (var n = 0; n < now.length; n++) {
        if (!now[n].hasAttribute('data-no-lazy')) { park(now[n]); io.observe(now[n]); }
      }
    });
    mo.observe(root, { childList: true, subtree: true });
  }

  window.NOPredatorLazyVideo = {
    wakeAll: function () { var a = vids(); for (var i2 = 0; i2 < a.length; i2++) wake(a[i2]); },
  };
})();
